import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { LuSettings, LuSave, LuEye, LuEyeOff, LuMap, LuCloud, LuBell } from 'react-icons/lu';

const GROUP_META = {
  integrations: { label: 'Integrations', icon: LuMap,      desc: 'Third-party API keys and service connections' },
  aws_s3:       { label: 'AWS S3',       icon: LuCloud,    desc: 'S3 bucket credentials for file storage (profile photos)', singleSave: true },
  firebase:     { label: 'Firebase',     icon: LuBell,     desc: 'Firebase Cloud Messaging — paste your Service Account JSON (FCM v1 API)' },
  general:      { label: 'General',      icon: LuSettings, desc: 'General application settings' },
};

const SAMPLE_SERVICE_ACCOUNT = `{
  "type": "service_account",
  "project_id": "your-project-id",
  "private_key_id": "abc123...",
  "private_key": "-----BEGIN PRIVATE KEY-----\\nMIIE...\\n-----END PRIVATE KEY-----\\n",
  "client_email": "firebase-adminsdk-xxx@your-project.iam.gserviceaccount.com",
  "client_id": "123456789",
  "auth_uri": "https://accounts.google.com/o/oauth2/auth",
  "token_uri": "https://oauth2.googleapis.com/token"
}`;

// Individual field with its own Save button
function SettingField({ setting, onSave }) {
  const [value,   setValue]   = useState(setting.value || '');
  const [visible, setVisible] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const dirty = value !== (setting.value || '');
  const isMultiline = setting.key === 'firebase_service_account';

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(setting.key, value); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-hajj-dark">{setting.label}</div>
          <div className="text-[11px] text-gray-400 font-mono mt-0.5">{setting.key}</div>
        </div>
        {dirty && (
          <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">unsaved</span>
        )}
      </div>
      {isMultiline ? (
        <div className="flex flex-col gap-2">
          <textarea
            value={value}
            onChange={e => setValue(e.target.value)}
            rows={8}
            className="input-field font-mono text-xs resize-y leading-relaxed"
            placeholder={SAMPLE_SERVICE_ACCOUNT}
            spellCheck={false}
          />
          <div className="flex justify-end">
            <button type="button" onClick={handleSave} disabled={saving || !dirty}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                dirty ? 'bg-hajj-dark text-white hover:bg-hajj-mid' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}>
              {saving ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <LuSave size={14} />}
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type={setting.type === 'secret' && !visible ? 'password' : 'text'}
              value={value}
              onChange={e => setValue(e.target.value)}
              className="input-field pr-10 w-full font-mono text-sm"
              placeholder={setting.type === 'secret' ? '••••••••••••••••••••' : `Enter ${setting.label}`}
            />
            {setting.type === 'secret' && (
              <button type="button" onClick={() => setVisible(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                {visible ? <LuEyeOff size={15} /> : <LuEye size={15} />}
              </button>
            )}
          </div>
          <button type="button" onClick={handleSave} disabled={saving || !dirty}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all flex-shrink-0 ${
              dirty ? 'bg-hajj-dark text-white hover:bg-hajj-mid' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}>
            {saving ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <LuSave size={14} />}
            Save
          </button>
        </div>
      )}
    </div>
  );
}

// Group of fields with a single Save All button at the bottom
function GroupSavePanel({ items, onSave }) {
  const initial = Object.fromEntries(items.map(s => [s.key, s.value || '']));
  const [values,  setValues]  = useState(initial);
  const [visible, setVisible] = useState({});
  const [saving,  setSaving]  = useState(false);
  const dirty = items.some(s => values[s.key] !== (s.value || ''));

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all(items.map(s => onSave(s.key, values[s.key])));
      toast.success('AWS S3 settings saved');
    } catch {
      // onSave already toasts the error
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {items.map(s => (
        <div key={s.key} className="flex flex-col gap-2">
          <div>
            <div className="text-sm font-semibold text-hajj-dark">{s.label}</div>
            <div className="text-[11px] text-gray-400 font-mono mt-0.5">{s.key}</div>
          </div>
          <div className="relative">
            <input
              type={s.type === 'secret' && !visible[s.key] ? 'password' : 'text'}
              value={values[s.key]}
              onChange={e => setValues(v => ({ ...v, [s.key]: e.target.value }))}
              className="input-field pr-10 w-full font-mono text-sm"
              placeholder={s.type === 'secret' ? '••••••••••••••••••••' : `Enter ${s.label}`}
            />
            {s.type === 'secret' && (
              <button type="button"
                onClick={() => setVisible(v => ({ ...v, [s.key]: !v[s.key] }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                {visible[s.key] ? <LuEyeOff size={15} /> : <LuEye size={15} />}
              </button>
            )}
          </div>
        </div>
      ))}
      <div className="flex justify-end pt-2">
        <button type="button" onClick={handleSave} disabled={saving || !dirty}
          className={`flex items-center gap-1.5 px-5 py-2 rounded-xl text-sm font-semibold transition-all ${
            dirty ? 'bg-hajj-dark text-white hover:bg-hajj-mid' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}>
          {saving ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <LuSave size={14} />}
          Save
        </button>
      </div>
    </div>
  );
}

export default function Settings() {
  const [settings, setSettings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/settings');
      setSettings(data.data || []);
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (key, value) => {
    try {
      await api.put(`/settings/${key}`, { value });
      setSettings(prev => prev.map(s => s.key === key ? { ...s, value } : s));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
      throw err;
    }
  };

  const grouped = settings.reduce((acc, s) => {
    const g = s.group || 'general';
    if (!acc[g]) acc[g] = [];
    acc[g].push(s);
    return acc;
  }, {});

  // Ordered tabs — only show groups that have settings
  const tabOrder = Object.keys(GROUP_META).filter(g => grouped[g]);
  const tabParam  = searchParams.get('tab');
  const currentTab = tabParam && tabOrder.includes(tabParam) ? tabParam : tabOrder[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
        Loading settings…
      </div>
    );
  }

  if (settings.length === 0) {
    return (
      <div className="card flex items-center justify-center h-32 text-gray-400 text-sm">
        No settings found
      </div>
    );
  }

  const tabItems  = grouped[currentTab] || [];
  const tabMeta   = GROUP_META[currentTab] || { label: currentTab, icon: LuSettings, desc: '' };

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <div className="flex items-center gap-2.5 text-hajj-dark">
        <LuSettings className="text-xl sm:text-2xl" />
        <h1 className="text-xl sm:text-2xl font-extrabold">Settings</h1>
      </div>

      <div className="card p-0 overflow-hidden">
        {/* Tab bar */}
        <div className="flex border-b border-gray-100 bg-hajj-light overflow-x-auto">
          {tabOrder.map(group => {
            const meta = GROUP_META[group] || { label: group, icon: LuSettings };
            const Icon = meta.icon;
            const active = group === currentTab;
            return (
              <button
                key={group}
                type="button"
                onClick={() => setSearchParams({ tab: group })}
                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-all ${
                  active
                    ? 'border-hajj-dark text-hajj-dark bg-white'
                    : 'border-transparent text-gray-400 hover:text-hajj-dark hover:bg-white/50'
                }`}
              >
                <Icon size={15} />
                {meta.label}
              </button>
            );
          })}
        </div>

        {/* Tab description */}
        {tabMeta.desc && (
          <div className="px-5 py-3 text-xs text-gray-400 border-b border-gray-50 bg-white">
            {tabMeta.desc}
          </div>
        )}

        {/* Tab content */}
        <div className="p-5">
          {tabMeta.singleSave ? (
            <GroupSavePanel key={currentTab} items={tabItems} onSave={handleSave} />
          ) : (
            <div className="flex flex-col divide-y divide-gray-50 -my-1">
              {tabItems.map(s => (
                <div key={s.key} className="py-4">
                  <SettingField setting={s} onSave={handleSave} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
